// App shell: login, hash routing, navigation, theme toggle.
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';
import { api, setSession, currentUser } from './api';
import { ErrorBox } from './ui';
import Dashboard from './pages/Dashboard';
import Timeline from './pages/Timeline';
import CalendarMonth from './pages/CalendarMonth';
import WeekRoster from './pages/WeekRoster';
import DayList from './pages/DayList';
import People from './pages/People';
import Attendance from './pages/Attendance';
import Leave from './pages/Leave';
import Accommodation from './pages/Accommodation';
import Demand from './pages/Demand';
import Schedule from './pages/Schedule';
import QuickAdd from './pages/QuickAdd';
import Catering from './pages/Catering';
import Travel from './pages/Travel';
import Reports from './pages/Reports';
import ImportExport from './pages/ImportExport';
import Settings, { Account } from './pages/Settings';

// Pages a job-scoped (external) account may open. The server enforces the same
// restriction independently — this list only keeps the interface honest.
const EXTERNAL_PAGES = [
  'schedule',
  'timeline', 'calendar', 'week', 'daylist',   // Planning
  'accommodation', 'demand', 'catering',       // Accommodation
  'account'
];

const ROUTES: { path: string; label: string; group?: string; el: () => React.ReactNode }[] = [
  { path: 'dashboard', label: 'Dashboard', el: () => <Dashboard /> },
  { path: 'quickadd', label: '⚡ Quick add bookings', el: () => <QuickAdd /> },
  { path: 'timeline', label: 'Workforce timeline', group: 'Planning', el: () => <Timeline /> },
  { path: 'calendar', label: 'Monthly calendar', el: () => <CalendarMonth /> },
  { path: 'week', label: 'Weekly site roster', el: () => <WeekRoster /> },
  { path: 'daylist', label: 'Daily site list', el: () => <DayList /> },
  { path: 'people', label: 'Personnel', group: 'Registers', el: () => <People /> },
  { path: 'attendance', label: 'Attendance periods', el: () => <Attendance /> },
  { path: 'leave', label: 'Leave', el: () => <Leave /> },
  { path: 'travel', label: 'Travel', el: () => <Travel /> },
  { path: 'accommodation', label: 'Accommodation', group: 'Accommodation', el: () => <Accommodation /> },
  { path: 'demand', label: 'Demand & deadlines', el: () => <Demand /> },
  { path: 'schedule', label: 'Booking schedule', el: () => <Schedule /> },
  { path: 'catering', label: 'Catering summary', el: () => <Catering /> },
  { path: 'reports', label: 'Reports', group: 'Data', el: () => <Reports /> },
  { path: 'import', label: 'Import & export', el: () => <ImportExport /> },
  { path: 'settings', label: 'Settings', el: () => <Settings /> },
  { path: 'account', label: 'My account', el: () => <Account /> }
];

function useHashRoute() {
  const [hash, setHash] = useState(window.location.hash.slice(2) || 'dashboard');
  useEffect(() => {
    const h = () => setHash(window.location.hash.slice(2) || 'dashboard');
    window.addEventListener('hashchange', h);
    return () => window.removeEventListener('hashchange', h);
  }, []);
  return hash.split('?')[0];
}

function Login({ onLogin }: { onLogin: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api('/auth/login', { method: 'POST', body: { username, password } });
      setSession(r.token, r.user);
      onLogin();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login-wrap">
      <form className="login-box" onSubmit={submit}>
        <h1><em>TPA</em> Workforce &amp; Accommodation Planner</h1>
        <p className="muted">Total Project Australia — Yulara Staff Accommodation</p>
        <ErrorBox error={error} />
        <div className="field" style={{ marginBottom: 10 }}>
          <span className="field-label">Username</span>
          <input className="input" value={username} onChange={e => setUsername(e.target.value)} autoFocus autoComplete="username" />
        </div>
        <div className="field" style={{ marginBottom: 16 }}>
          <span className="field-label">Password</span>
          <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
        </div>
        <button className="btn btn-primary" style={{ width: '100%' }} disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <p className="muted" style={{ marginTop: 14 }}>First time? Sign in with admin / admin123 and change the password in Settings.</p>
      </form>
    </div>
  );
}

function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [theme, setTheme] = useState(localStorage.getItem('tpa_theme') || 'light');
  const route = useHashRoute();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('tpa_theme', theme);
  }, [theme]);

  useEffect(() => {
    api('/auth/me').then(u => { setSession(localStorage.getItem('tpa_token'), u); setAuthed(true); })
      .catch(() => setAuthed(false));
    const onLogout = () => setAuthed(false);
    window.addEventListener('tpa-logout', onLogout);
    return () => window.removeEventListener('tpa-logout', onLogout);
  }, []);

  if (authed === null) return null;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const scoped = (currentUser as any)?.scoped === true;
  // Internal users reach their password via Settings, so the standalone account page is
  // kept out of their menu to avoid duplicating it.
  const routes = scoped
    ? ROUTES.filter(r => EXTERNAL_PAGES.includes(r.path))
    : ROUTES.filter(r => r.path !== 'account');
  const active = routes.find(r => r.path === route) || routes[0];

  return (
    <div className="app">
      <aside className="sidebar no-print">
        <div className="sidebar-brand">
          <div className="logo">TOTAL PROJECT <em>AUSTRALIA</em></div>
          <div className="sub">Workforce &amp; Accommodation Planner</div>
        </div>
        <nav className="nav">
          {routes.map(r => (
            <React.Fragment key={r.path}>
              {r.group && <div className="nav-group">{r.group}</div>}
              <a href={`#/${r.path}`} className={active.path === r.path ? 'active' : ''}>{r.label}</a>
            </React.Fragment>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div>
            {currentUser?.display_name}{' '}
            <span className="muted">({scoped ? 'external' : currentUser?.role})</span>
          </div>
          {scoped && (currentUser as any)?.company && (
            <div className="muted">{(currentUser as any).company}</div>
          )}
          <div className="btn-row" style={{ marginTop: 8 }}>
            <button className="btn btn-sm" onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
              {theme === 'light' ? '🌙 Dark' : '☀️ Light'}
            </button>
            <button className="btn btn-sm" onClick={async () => {
              try { await api('/auth/logout', { method: 'POST' }); } catch {}
              setSession(null, null);
              setAuthed(false);
            }}>Sign out</button>
          </div>
        </div>
      </aside>
      <main className="main">{active.el()}</main>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
